// src/components/LanguageSwitcher.tsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type LangCode = "en" | "it" | "es";

const LANGS: { code: LangCode; labelKey: string; flag: string }[] = [
  { code: "en", labelKey: "common.language.en", flag: "🇬🇧" },
  { code: "it", labelKey: "common.language.it", flag: "🇮🇹" },
  { code: "es", labelKey: "common.language.es", flag: "🇪🇸" }
];

const LanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const normalized = (i18n.language || "en").split("-")[0] as LangCode;
  const current = LANGS.find((l) => l.code === normalized) ?? LANGS[0];

  const handleSelect = (lng: LangCode) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  // Close on outside click / Esc
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="lang-dropdown" ref={containerRef}>
      <button
        type="button"
        className="lang-dropdown-button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="lang-dropdown-label">{t(current.labelKey)}</span>
        <span className="lang-dropdown-chevron">▾</span>
      </button>

      {open && (
        <div className="lang-dropdown-menu" role="listbox">
          {LANGS.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={
                lang.code === current.code
                  ? "lang-dropdown-item lang-dropdown-item-active"
                  : "lang-dropdown-item"
              }
              onClick={() => handleSelect(lang.code)}
              role="option"
              aria-selected={lang.code === current.code}
            >
              <span className="lang-dropdown-flag">{lang.flag}</span>
              <span className="lang-dropdown-item-label">
                {t(lang.labelKey)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
